import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

function EmbedFaqPage() {
  const { publicId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/public/faq-sets/${encodeURIComponent(publicId || '')}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.response?.data?.detail || 'Unable to load FAQs.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [publicId]);

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center bg-white text-slate-500 text-sm">
        Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[200px] flex items-center justify-center bg-white text-slate-600 text-sm px-4 text-center">
        {error || 'Not found.'}
      </div>
    );
  }

  const theme = (data.style?.theme || 'minimal').toLowerCase();
  const mode = (data.style?.accordion_mode || 'multiple').toLowerCase();
  const accent = (data.style?.accent_color || '').trim();

  const shell =
    theme === 'card'
      ? 'rounded-xl shadow-md border border-slate-200 bg-white p-4 sm:p-6'
      : theme === 'bordered'
        ? 'rounded-xl border-2 border-slate-200 bg-slate-50/80 p-4 sm:p-6'
        : 'rounded-xl border border-slate-200 bg-white p-4 sm:p-6';

  const accentStyle = accent
    ? { ['--faq-accent']: accent, borderColor: 'var(--faq-accent)' }
    : undefined;

  return (
    <div className="min-h-0 bg-transparent text-slate-900 antialiased" style={accentStyle}>
      <div className={shell} style={accent ? { borderColor: accent } : undefined}>
        <header className="mb-4 space-y-1">
          <h1 className="text-lg font-semibold text-slate-900 leading-tight">{data.title}</h1>
          {data.subtitle ? (
            <p className="text-sm text-slate-600">{data.subtitle}</p>
          ) : null}
          {data.description ? (
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{data.description}</p>
          ) : null}
        </header>
        {data.items?.length ? (
          <Accordion
            type={mode === 'single' ? 'single' : 'multiple'}
            collapsible={mode === 'single'}
            className="w-full"
          >
            {data.items.map((item, i) => (
              <AccordionItem value={`faq-${i}`} key={i} className="border-slate-200">
                <AccordionTrigger
                  className="text-left text-sm hover:no-underline"
                  style={accent ? { color: accent } : undefined}
                >
                  {item.question}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                    {item.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <p className="text-sm text-slate-500">No questions yet.</p>
        )}
      </div>
    </div>
  );
}

export default EmbedFaqPage;
